import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { X, Mail, Loader2, Check } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "shx_newsletter_dismissed_until";
const COOLDOWN_DAYS = 30;

// Returns true if a previously stored cooldown timestamp is still active
const isCoolingDown = () => {
  try {
    const until = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
    return until && Date.now() < until;
  } catch {
    return false;
  }
};

const setCooldown = () => {
  try {
    const until = Date.now() + COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(STORAGE_KEY, String(until));
  } catch {
    /* ignore */
  }
};

export const NewsletterPopup = () => {
  const [settings, setSettings] = useState(null);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Fetch settings once
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/newsletter/settings`)
      .then((res) => {
        if (!cancelled) setSettings(res.data);
      })
      .catch(() => {
        /* silent */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Schedule open
  useEffect(() => {
    if (!settings?.enabled) return;
    if (isCoolingDown()) return;
    const delay = Math.max(0, (settings.delay_seconds ?? 4) * 1000);
    const t = setTimeout(() => {
      setOpen(true);
      trackEvent("newsletter_popup_shown");
    }, delay);
    return () => clearTimeout(t);
  }, [settings]);

  // Lock body scroll when popup open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!settings) return null;

  const close = () => {
    setOpen(false);
    setCooldown();
    trackEvent("newsletter_popup_dismissed", { had_value: !!email });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        email: email.trim(),
        name: name.trim(),
        website: honeypot,
      });
      trackEvent("newsletter_subscribed", { email_domain: email.split("@")[1] });
      setSuccess(true);
      setCooldown();
      // Auto-close after 2.5s
      setTimeout(() => setOpen(false), 2500);
    } catch (err) {
      // 429 = honeypot triggered → silently close
      if (err?.response?.status === 429) {
        setOpen(false);
        return;
      }
      // Generic fallback
      alert(err?.response?.data?.detail || "Errore. Riprova fra poco.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="newsletter-popup"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={close}
          className="fixed inset-0 z-[150] bg-[#2A1810]/55 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 24 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-[#FBF6EA] border border-[#D9C5A4] rounded-sm max-w-md w-full shadow-2xl overflow-hidden"
          >
            <button
              onClick={close}
              data-testid="newsletter-popup-close"
              aria-label="chiudi"
              className="absolute top-3 right-3 text-[#6E5B4A] hover:text-[#A0522D] transition p-2 z-10"
            >
              <X size={18} />
            </button>

            {/* Decorative azure blob */}
            <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-[#7AB8D6]/40 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full bg-[#A0522D]/20 blur-3xl pointer-events-none" />

            <div className="relative p-7 sm:p-9">
              {success ? (
                <div className="text-center py-6">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", duration: 0.6 }}
                    className="w-14 h-14 mx-auto rounded-full border-2 border-[#A0522D] flex items-center justify-center mb-5"
                  >
                    <Check size={26} className="text-[#A0522D]" />
                  </motion.div>
                  <h3 className="font-serif-display text-3xl text-[#2A1810]">
                    Ci sei!
                  </h3>
                  <p className="mt-3 text-[#6E5B4A] text-sm leading-relaxed">
                    {settings.success_message}
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-11 h-11 rounded-full bg-[#A0522D] text-[#FBF6EA] flex items-center justify-center">
                      <Mail size={18} />
                    </div>
                    <p className="overline text-[#4F8FA8]">Newsletter</p>
                  </div>

                  <h3 className="font-serif-display text-3xl sm:text-4xl text-[#2A1810] leading-tight">
                    {settings.title}
                  </h3>
                  <p className="mt-4 text-[#6E5B4A] text-sm leading-relaxed">
                    {settings.subtitle}
                  </p>

                  <form onSubmit={submit} className="mt-6 space-y-3">
                    {/* Honeypot field, hidden from humans */}
                    <div
                      aria-hidden="true"
                      style={{
                        position: "absolute",
                        left: "-9999px",
                        width: "1px",
                        height: "1px",
                        overflow: "hidden",
                        opacity: 0,
                        pointerEvents: "none",
                      }}
                    >
                      <input
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={honeypot}
                        onChange={(e) => setHoneypot(e.target.value)}
                      />
                    </div>

                    <input
                      data-testid="newsletter-popup-name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Nome (opzionale)"
                      maxLength={80}
                      className="input-warm"
                    />
                    <input
                      data-testid="newsletter-popup-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="La tua email"
                      className="input-warm"
                    />
                    <button
                      type="submit"
                      disabled={submitting || !email.trim()}
                      data-testid="newsletter-popup-submit"
                      className="btn-primary w-full justify-center disabled:opacity-40"
                    >
                      {submitting ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Mail size={14} />
                      )}
                      {settings.button_label}
                    </button>
                  </form>

                  <p className="mt-4 text-[10px] text-[#A8987F] text-center tracking-wide">
                    Niente spam. Solo aggiornamenti su workshop e date.
                  </p>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
